import { bookingInquiryStrings } from '@data/bookingInquiry';
import { receivingCityOptions } from '@data/cities';
import { preAlertStrings } from '@data/preAlert';
import { availableServiceNames, routePlanStrings } from '@data/routePlan';
import { companyItem } from '@models/Company';
import { contactForm, contactFormLoose, createValidateContactForm } from '@models/Contact';
import { country } from '@models/Country';
import {
  customsInstructionBase,
  customsInstructionForm,
  customsInstructionFormInitialValues,
} from '@models/CustomsInstruction';
import {
  deliveryInstructionBase,
  deliveryInstructionForm,
  deliveryInstructionFormInitialValues,
} from '@models/DeliveryInstruction';
import { PreAlertDetails } from '@models/next/PreAlert';
import { rolesByCountry } from '@models/Role';
import {
  bookingInquiryRoutePlan,
  routePlanOrder,
  serviceName,
  simulatorResult,
} from '@models/RoutePlan';
import { useTradingAgency } from '@models/TradingAgency';
import { adminUser, BaseUser, minimumUser, UserHelper } from '@models/User';
import { firstLeftEither } from '@utils/errors';
import {
  MessagesEither,
  makeMessagesEitherRightIf,
  makeMessagesEitherLeft,
  mapMessagesEitherLeftOr,
  mergeMessagesEither,
  makeMessagesEitherLeftIf,
} from '@utils/errors';
import {
  createField,
  createValidate,
  foreignKeyField,
  formStrings,
  newEmailField,
  omittableStringField,
  remoteFileField,
  requiredNumberField,
  requiredPositiveIntegerField,
  requiredPositiveNumberField,
  requiredStringField,
  stringField,
} from '@utils/forms';
import { createStrings } from '@utils/local';
import { id, validateEnglish } from '@utils/types';
import dayjs from 'dayjs';
import { isLeft } from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

export type BookingInquiryAdminGroupNames =
  | 'shipper'
  | 'receiver'
  | 'consignee'
  | 'customerNo'
  | 'cargos'
  | 'miscInfo'
  | 'specialNote'
  | 'routePlan';

export const bookingInquirySource = t.union([t.literal('admin'), t.literal('client')]);

export type BookingInquirySource = t.TypeOf<typeof bookingInquirySource>;

export const bookingInquiryGetQueryOrdering = t.union([
  t.literal('-created_at'),
  t.literal('created_at'),
]);

export const bookingInquiryGetQuery = t.type({
  author: h.omittable(t.string),
  d2d_no: h.omittable(t.string),
  ordering: h.omittable(bookingInquiryGetQueryOrdering),
  page: h.omittable(t.number),
  status: h.omittable(t.string),
});

const strings = createStrings({
  ja: {
    title: '詳細',
    customerIntelligence: '顧客情報',
    companyName: '会社名',
    memberNo: '会員番号',
    author: '申込者',
    mobileNum: '携帯電話番号',
    phoneNum: '固定電話番号',
    email: 'メールアドレス',
    thisField: 'この欄',
  },
});

export const inquiryPlaceholderStrings = createStrings({
  ja: {
    chinaCompanyInfomation:
      '例：\n広東省東莞市\n厚街鎮東方一路777号\n南方原材料有限公司\n電話 138〇〇〇〇〇〇〇〇\n担当 南さんwechat ID:',
    baggageAddress:
      '例：\n広東省東莞市\n厚街鎮東方一路777号\n南方原材料有限公司\n電話 138〇〇〇〇〇〇〇〇\n担当 南さんwechat ID:',
    baggageDetail:
      '例：\n品名：携帯電話のケース\n個数：100カートン\n大きさ：50cm x 50cm x 40cm\n一個の重量：12KG',
    arriveChinaDate: '例：大体の貨物出来上がりは3月下旬です',
    deliveryDate: '例：5月12日です',
    deliveryDateFill: '例：東京都XXX区XXX1丁目-1-1',
    deliveryJapanDate: '例：東京都〇〇〇区〇〇〇1丁目-1-1',
    specialTips: '特記事項をご入力ください。',
    remarks:
      '例：\n①長尺（1辺の長さが2m以上ある）\n②重量（1個の重さが1000kg以上ある） \n③ワレモノ \n④日本での保管を相談したい',
    email: 'メールアドレスをご入力ください。',
    file: '',
  },
});

export const inquiryPaymentTimeType = t.union([
  t.literal('時間順 新→古'),
  t.literal('時間順 古→新'),
]);
export const allInquiryPaymentTimeType = inquiryPaymentTimeType.types.map((l) => l.value);

export const inquiryType = t.union([t.literal('相談'), t.literal('時間順 古→新')]);
export const allInquiryType = inquiryType.types.map((l) => l.value);

export const inquiryStatus = t.union([
  t.literal('未対応'),
  t.literal('進行中の案件を作成'),
  t.literal('FCキャンセル済'),
  t.literal('客側キャンセル済'),
  t.literal('対応中'),
]);

export type InquiryStatus = t.TypeOf<typeof inquiryStatus>;

export const allInquiryStatus = inquiryStatus.types.map((l) => l.value);

export const clientInquiryStatusMap = (status: string) => {
  switch (status) {
    case id<InquiryStatus>('未対応'):
    case id<InquiryStatus>('対応中'):
      return '確認中';
    case id<InquiryStatus>('進行中の案件を作成'):
      return '予約確定';
    case id<InquiryStatus>('FCキャンセル済'):
      return 'キャンセルされた';
    case id<InquiryStatus>('客側キャンセル済'):
      return 'キャンセルした';
    default:
      return status;
  }
};

export const inquiryViewerType = t.union([
  t.literal('creator'),
  t.literal('shipper'),
  t.literal('consignee'),
  t.literal('follower'),
  t.literal('owner'),
]);

export const inquiryViewer = t.type({
  id: h.omittable(t.number),
  can_view_type: inquiryViewerType,
  user: h.omittable(t.number),
  company_name: t.string,
  email: t.string,
});

export type InquiryViewer = t.TypeOf<typeof inquiryViewer>;

export class BookingInquiryFormFields {
  static instance: BookingInquiryFormFields | undefined;

  static get shared() {
    if (BookingInquiryFormFields.instance === undefined) {
      BookingInquiryFormFields.instance = new BookingInquiryFormFields();
    }
    return BookingInquiryFormFields.instance;
  }

  companyName = createField((label) => requiredStringField(label, 1, 128), 'companyName', {
    ja: strings.companyName,
  });

  memberNo = createField((label) => requiredStringField(label, 1, 128), 'memberNo', {
    ja: strings.memberNo,
  });

  author = createField((label) => requiredStringField(label, 1, 128), 'author', {
    ja: strings.author,
  });

  mobileNum = createField((label) => requiredStringField(label, 1, 128), 'mobileNum', {
    ja: strings.mobileNum,
  });

  phoneNum = createField((label) => requiredStringField(label, 1, 128), 'phoneNum', {
    ja: strings.phoneNum,
  });

  email = createField((label) => requiredStringField(label, 1, 128), 'email', {
    ja: strings.email,
  });
}

export const adminInquiry = t.type({
  id: h.omittable(t.number),
  d2d_no: h.omittable(t.string),
  member_no: h.omittable(t.string),
  created_at: h.omittable(t.string),
  status: h.omittable(t.string),
  english_name: t.string,
  company_name: h.omittable(t.string),
  member_salesperson: h.omittable(t.string),
  salesperson_china_name: h.omittable(t.string),
  salesperson_china_last_name: h.omittable(t.string),
  salesperson_japan_name: h.omittable(t.string),
  salesperson_japan_last_name: h.omittable(t.string),
  result: h.omittable(t.string),
  type: h.omittable(t.string),
  consignee: h.omittable(t.string),
  user: adminUser,
  owner: h.omittable(adminUser),
  owner_company: h.omittable(companyItem),
  source: bookingInquirySource,
  agency_name: h.omittable(t.string),
});

export type AdminInquiry = t.TypeOf<typeof adminInquiry>;

export const bookingInquiryStatus = t.type({
  status: inquiryStatus,
});

export type BookingInquiryStatus = t.TypeOf<typeof bookingInquiryStatus>;

export const bookingInquiryMemo = t.type({
  memo: t.string,
});

export type BookingInquiryMemo = t.TypeOf<typeof bookingInquiryMemo>;

export const inquirySearchType = t.type({
  page: h.omittable(h.number().cast()),
  member_no: h.omittable(h.string()),
  booking_no: h.omittable(h.string()),
  ordering: h.omittable(h.string()),
  created_at_before: h.omittable(h.string()),
  created_at_after: h.omittable(h.string()),
  status: h.omittable(inquiryStatus),
});

export const inquiryCancelDetailBody = t.type({
  cancel_detail: t.string,
});

export const cargo = h.taggedUnion([
  {
    tag: t.literal('LWH'),
    name: requiredStringField(bookingInquiryStrings.cargoName),
    num: requiredPositiveIntegerField(bookingInquiryStrings.cargoNum),
    length: requiredPositiveNumberField(bookingInquiryStrings.length),
    width: requiredPositiveNumberField(bookingInquiryStrings.width),
    height: requiredPositiveNumberField(bookingInquiryStrings.height),
    weight: requiredPositiveNumberField(bookingInquiryStrings.weight),
  },
  {
    tag: t.literal('WV'),
    name: requiredStringField(bookingInquiryStrings.cargoName),
    num: requiredPositiveIntegerField(bookingInquiryStrings.cargoNum),
    total_volume: requiredPositiveNumberField(bookingInquiryStrings.totalVolume),
    total_weight: requiredPositiveNumberField(bookingInquiryStrings.totalWeight),
  },
]);

export const cargoLoose = h.taggedUnion([
  {
    tag: t.literal('LWH'),
    name: t.string,
    num: h.omittable(t.number),
    length: h.omittable(t.number),
    width: h.omittable(t.number),
    height: h.omittable(t.number),
    weight: h.omittable(t.number),
  },
  {
    tag: t.literal('WV'),
    name: t.string,
    num: h.omittable(t.number),
    total_volume: h.omittable(t.number),
    total_weight: h.omittable(t.number),
  },
]);

export type CargoLoose = t.TypeOf<typeof cargoLoose>;

export type Cargo = t.TypeOf<typeof cargo>;

export const cargosByPickupAddressForm = h
  .array(
    t.type({
      pickup_address: foreignKeyField(bookingInquiryStrings.pickupAddress, { required: true }),
      pickup_address_input: h.omittable(contactForm),
      cargos: h.array(cargo).min(1, bookingInquiryStrings.atLeastOneCargo),
    }),
  )
  .min(1, bookingInquiryStrings.atLeastOnePickupAddress);

export type CargosByPickupAddressForm = t.TypeOf<typeof cargosByPickupAddressForm>;

export const cargosByPort = t.array(
  t.type({
    port_name: t.string,
    cargos: t.array(cargoLoose),
  }),
);

export type CargosByPort = t.TypeOf<typeof cargosByPort>;

export const cargosByPickupAddress = t.array(
  t.type({
    pickup_address: contactFormLoose,
    cargos: t.array(cargoLoose),
  }),
);

export type CargosByPickupAddress = t.TypeOf<typeof cargosByPickupAddress>;

export const cargosByPickupAddressPatch = t.array(
  t.type({
    pickup_address: contactForm,
    cargos: t.array(cargoLoose),
  }),
);

export type CargosByPickupAddressPatch = t.TypeOf<typeof cargosByPickupAddressPatch>;

export const cargo2 = h.taggedUnion([
  {
    id: t.number,
    tag: t.literal('LWH'),
    name: requiredStringField(bookingInquiryStrings.cargoName).refine(
      validateEnglish,
      formStrings.englishPrompt,
    ),
    num: requiredPositiveIntegerField(bookingInquiryStrings.cargoNum),
    length: requiredPositiveNumberField(bookingInquiryStrings.length),
    width: requiredPositiveNumberField(bookingInquiryStrings.width),
    height: requiredPositiveNumberField(bookingInquiryStrings.height),
    weight: requiredPositiveNumberField(bookingInquiryStrings.weight),
    pickup_address_index: requiredNumberField(bookingInquiryStrings.pickupAddress),
  },
  {
    id: t.number,
    tag: t.literal('WV'),
    name: requiredStringField(bookingInquiryStrings.cargoName).refine(
      validateEnglish,
      formStrings.englishPrompt,
    ),
    num: requiredPositiveIntegerField(bookingInquiryStrings.cargoNum),
    total_volume: requiredPositiveNumberField(bookingInquiryStrings.totalVolume),
    total_weight: requiredPositiveNumberField(bookingInquiryStrings.totalWeight),
    pickup_address_index: requiredNumberField(bookingInquiryStrings.pickupAddress),
  },
]);

export type Cargo2 = t.TypeOf<typeof cargo2>;

export const bookingInquiryCargosPickupAddressInput = t.type({
  id: t.number,
  contact: contactForm,
});

export type BookingInquiryCargosPickupAddressInput = t.TypeOf<
  typeof bookingInquiryCargosPickupAddressInput
>;

export const bookingInquiryForm = t.type({
  shipper: foreignKeyField(bookingInquiryStrings.shipper, { required: false }),
  shipper_input: h.omittable(contactForm),
  consignee: foreignKeyField(bookingInquiryStrings.consignee, { required: false }),
  consignee_input: h.omittable(contactForm),
  receiver: foreignKeyField(bookingInquiryStrings.receiver, { required: false }),
  receiver_input: h.omittable(contactForm),
  trade_condition: requiredStringField(bookingInquiryStrings.tradeCondition),
  customer_no: stringField(bookingInquiryStrings.customerNo),
  use_trading_agency: useTradingAgency,
  trading_agency_payer: country,
  use_rcep: h.boolean().booleanish(),
  preferred_arrival_date: stringField(bookingInquiryStrings.preferredArrivalDate),
  file: h.array(remoteFileField(bookingInquiryStrings.additionalFile)),
  has_pickups: h.boolean().booleanish(),
  cargos2: h
    .array(cargo2)
    .min(1, bookingInquiryStrings.atLeastOneCargo)
    .max(20, bookingInquiryStrings.maxCargos),
  cargos_port_name: stringField(bookingInquiryStrings.portName),
  cargos_pickup_addresses: h.array(t.number),
  cargos_pickup_address_inputs: h.array(bookingInquiryCargosPickupAddressInput),
  pickup_date: omittableStringField(bookingInquiryStrings.pickupDate),
  move_in_date: omittableStringField(bookingInquiryStrings.moveInDate),
  route_plan: foreignKeyField(routePlanStrings.route, { required: true }),
  route_plan_order: routePlanOrder,
  service_names: h.array(serviceName),
  receiving_warehouse_cities: h.array(t.string),
  route_plan_simulator_result: h.omittable(simulatorResult),
  is_caution_checked: h.array(t.string),
  customs_instruction: t.type({
    has_special_note: customsInstructionForm.props.has_special_note,
    special_note: customsInstructionForm.props.special_note,
    special_note_titles: customsInstructionForm.props.special_note_titles,
  }),
  delivery_instruction: t.type({
    has_special_note: deliveryInstructionForm.props.has_special_note,
    special_note: deliveryInstructionForm.props.special_note,
  }),
  has_special_note: h.boolean(),
  special_note: h.omittable(stringField(bookingInquiryStrings.specialNote)),
});

export type BookingInquiryForm = t.TypeOf<typeof bookingInquiryForm>;

export const bookingInquiryInitialValues: BookingInquiryForm = {
  shipper: undefined,
  shipper_input: undefined,
  consignee: undefined,
  consignee_input: undefined,
  receiver: undefined,
  receiver_input: undefined,
  trade_condition: '',
  customer_no: '',
  is_caution_checked: [],
  use_trading_agency: 'UNUSED',
  trading_agency_payer: 'cn',
  preferred_arrival_date: '',
  file: [],
  has_pickups: 'false' as any,
  cargos_port_name: '',
  cargos2: [],
  cargos_pickup_addresses: [],
  cargos_pickup_address_inputs: [],
  pickup_date: '',
  move_in_date: '',
  route_plan_order: 'RECOMMENDED',
  service_names: availableServiceNames,
  receiving_warehouse_cities: receivingCityOptions.map((o) => o.name),
  route_plan: -1, // initialize as -1 to pass validation
  route_plan_simulator_result: undefined,
  use_rcep: 'false' as any,
  customs_instruction: customsInstructionFormInitialValues,
  delivery_instruction: deliveryInstructionFormInitialValues,
  has_special_note: false,
  special_note: '',
};

export const adminCargosForm = t.type({
  has_pickups: bookingInquiryForm.props.has_pickups,
  cargos2: bookingInquiryForm.props.cargos2,
  pickup_addresses: h.array(contactFormLoose),
  cargos_port_name: bookingInquiryForm.props.cargos_port_name,
  pickup_date: bookingInquiryForm.props.pickup_date,
  move_in_date: bookingInquiryForm.props.move_in_date,
});

export type AdminCargosForm = t.TypeOf<typeof adminCargosForm>;

export const validateAdminCargosForm = createValidate(adminCargosForm, {
  fields: { pickup_addresses: createValidateContactForm('pickup_address') },
});

export const getDeliveryWeight = (volumeM3: number, weight: number) => {
  return Math.max(volumeM3 * 280, weight);
};

export const getSummaryOfCargoLoose = (input: CargoLoose) => {
  const e = cargoLoose.decode({
    ...input,
    name: 'NAME', // trick: placehold name in case it is missing in user input
  });

  if (isLeft(e)) {
    return {
      unit: undefined,
      cbm: undefined,
      kg: undefined,
    };
  }

  const decoded = e.right;

  if (decoded.tag === 'LWH') {
    const volumeM3 =
      ((decoded.num ?? 0) * (decoded.length ?? 0) * (decoded.width ?? 0) * (decoded.height ?? 0)) /
      1000_000;

    return {
      unit: decoded.num,
      cbm: volumeM3,
      kg: (decoded.num ?? 0) * (decoded.weight ?? 0),
    };
  }
  if (input.tag === 'WV') {
    return {
      unit: decoded.num,
      cbm: decoded.total_volume,
      kg: decoded.total_weight ?? 0,
    };
  }
  throw new Error('Unknown tag');
};

export const getSummaryOfCargo2 = (input: Cargo2) => {
  const e = cargo2.decode({
    ...input,
    name: 'NAME', // trick: placehold name in case it is missing in user input
  });

  if (isLeft(e)) {
    return {
      unit: undefined,
      cbm: undefined,
      kg: undefined,
    };
  }

  const decoded = e.right;

  if (decoded.tag === 'LWH') {
    const volumeM3 =
      ((decoded.num ?? 0) * (decoded.length ?? 0) * (decoded.width ?? 0) * (decoded.height ?? 0)) /
      1000_000;

    return {
      unit: decoded.num,
      cbm: volumeM3,
      kg: (decoded.num ?? 0) * (decoded.weight ?? 0),
    };
  }
  if (input.tag === 'WV') {
    return {
      unit: decoded.num,
      cbm: decoded.total_volume,
      kg: decoded.total_weight ?? 0,
    };
  }
  throw new Error('Unknown tag');
};

export type CargoSummary = ReturnType<typeof getSummaryOfCargo2>;

export const getDefaultCargoForm = (tag: 'LWH' | 'WV'): Cargo2 =>
  tag === 'LWH'
    ? {
        id: Math.floor(Math.random() * 100000),
        tag,
        name: '',
        num: 1,
        length: 1,
        width: 1,
        height: 1,
        weight: 1,
        pickup_address_index: 1,
      }
    : {
        id: Math.floor(Math.random() * 100000),
        tag,
        name: '',
        num: 1,
        total_volume: 1,
        total_weight: 1,
        pickup_address_index: 1,
      };

export const tradeCondtion = t.union([
  t.literal('DDP'), // ddp
  t.literal('EXW'), // exw
  t.literal('DDU'), // ddu 検査料日本払い
  t.literal('FCA'), // fca
  t.literal('DDU_CHINA'), // ddu 検査料中国払い
]);

export type TradeCondition = t.TypeOf<typeof tradeCondtion>;

export const allTradeConditions = tradeCondtion.types.map((type) => type.value);

export const inquiryDetailCargos = t.type({
  cargos_by_port: h.omittable(cargosByPort),
  cargos_by_pickup_address: h.omittable(cargosByPickupAddress),
});

export type InquiryDetailCargos = t.TypeOf<typeof inquiryDetailCargos>;

export const inquiryDetailCargosPatch = t.type({
  cargos_by_port: h.omittable(cargosByPort),
  cargos_by_pickup_address: h.omittable(cargosByPickupAddressPatch),
});

export type InquiryDetailCargosPatch = t.TypeOf<typeof inquiryDetailCargosPatch>;

export const mapAdminInquiryDetailToAdminCargosForm = (
  input: AdminInquiryDetail,
): AdminCargosForm => {
  const mapCargoLooseToCargo2 = (cargoLoose: CargoLoose, pickup_address_index: number): Cargo2 => {
    const defaultCargo = getDefaultCargoForm(cargoLoose.tag);

    if (defaultCargo.tag === 'LWH' && cargoLoose.tag === 'LWH') {
      return {
        ...defaultCargo,
        ...cargoLoose,
        num: cargoLoose.num ?? defaultCargo.num,
        length: cargoLoose.length ?? defaultCargo.length,
        width: cargoLoose.width ?? defaultCargo.width,
        height: cargoLoose.height ?? defaultCargo.height,
        weight: cargoLoose.weight ?? defaultCargo.weight,
        pickup_address_index,
      };
    }

    if (defaultCargo.tag === 'WV' && cargoLoose.tag === 'WV') {
      return {
        ...defaultCargo,
        ...cargoLoose,
        num: cargoLoose.num ?? defaultCargo.num,
        total_volume: cargoLoose.total_volume ?? defaultCargo.total_volume,
        total_weight: cargoLoose.total_weight ?? defaultCargo.total_weight,
      };
    }

    throw new Error();
  };

  const formCargos =
    input.cargos.cargos_by_pickup_address
      ?.map((p, i) => p.cargos.map((c) => mapCargoLooseToCargo2(c, i + 1)))
      .flat() ??
    input.cargos
      .cargos_by_port!.map((p) => p.cargos.map((c) => mapCargoLooseToCargo2(c, 1)))
      .flat();

  return {
    has_pickups: (input.cargos.cargos_by_pickup_address ? 'true' : 'false') as any,
    pickup_addresses: input.cargos.cargos_by_pickup_address
      ? input.cargos.cargos_by_pickup_address.map((p, i) => ({
          ...p.pickup_address,
          editing: false,
          id: i + 1,
        }))
      : [],
    cargos2: formCargos,
    cargos_port_name:
      input.cargos.cargos_by_port?.[0].port_name ?? input.route_plan?.receiving_city ?? '',
    move_in_date: input.move_in_date ?? '',
    pickup_date: input.pickup_date ?? '',
  };
};

export const mapAdminCargosFormToInquiryDetailCargosPatch = (
  input: AdminCargosForm,
): InquiryDetailCargosPatch => {
  if (input.has_pickups) {
    const cargos_by_pickup_address: CargosByPickupAddress = [];

    let pickupAddressIndex = 1;
    for (const pickupAddress of input.pickup_addresses) {
      const item = {
        pickup_address: pickupAddress,
        cargos: input.cargos2.filter((c) => c.pickup_address_index === pickupAddressIndex),
      };
      if (item.cargos.length > 0) {
        cargos_by_pickup_address.push(item);
      }

      pickupAddressIndex++;
    }

    return {
      cargos_by_port: undefined,
      cargos_by_pickup_address,
    };
  } else {
    return {
      cargos_by_port: [
        {
          port_name: input.cargos_port_name,
          cargos: input.cargos2,
        },
      ],
      cargos_by_pickup_address: undefined,
    };
  }
};

export const baseInquiryDetail = t.type({
  shipper: h.omittable(contactFormLoose),
  consignee: h.omittable(contactFormLoose),
  receiver: h.omittable(contactFormLoose),
  has_pickups: t.boolean,
  cargos: inquiryDetailCargos,
  trade_condition: tradeCondtion,
  customer_no: t.string,
  is_caution_checked: t.boolean,
  use_trading_agency: useTradingAgency,
  trading_agency_payer: country,
  use_rcep: h.omittable(t.boolean),
  preferred_arrival_date: t.string,
  file: h.omittable(t.string),
  can_views: h.array(inquiryViewer),
  is_invoice_done: t.boolean,
  is_payment_slip_done: t.boolean,
  route_plan: h.omittable(bookingInquiryRoutePlan),
  route_plan_simulator_result: h.omittable(simulatorResult),
  pickup_date: h.omittable(t.string),
  move_in_date: h.omittable(t.string),
  customs_instruction: h.omittable(
    t.type({
      has_special_note: customsInstructionBase.props.has_special_note,
      special_note: customsInstructionBase.props.special_note,
    }),
  ),
  delivery_instruction: h.omittable(
    t.type({
      has_special_note: deliveryInstructionBase.props.has_special_note,
      special_note: deliveryInstructionBase.props.special_note,
    }),
  ),
  has_special_note: t.boolean,
  special_note: h.omittable(t.string),
});

export type BaseInquiryDetail = t.TypeOf<typeof baseInquiryDetail>;

export const bookingInquiryListItem = t.type({
  id: t.number,
  d2d_no: t.string,
  created_at: t.string,
  customer_no: baseInquiryDetail.props.customer_no,
  user: h.omittable(minimumUser),
  shipper: t.string,
  consignee: t.string,
  status: t.string,
});

export const bookingInquiryPages = t.type({
  count: t.number,
  next: h.omittable(t.string),
  previous: h.omittable(t.string),
  results: t.array(bookingInquiryListItem),
});

export const inquiryDetail = t.type({
  ...baseInquiryDetail.props,
  id: t.number,
  d2d_no: t.string,
  user: h.omittable(minimumUser),
  salesperson: h.omittable(t.string),
  memo: h.omittable(t.string),
  status: inquiryStatus,
  updated_at: h.omittable(t.string),
  created_at: h.omittable(t.string),
});

export type InquiryDetail = t.TypeOf<typeof inquiryDetail>;

export const adminInquiryDetail = t.type({
  ...baseInquiryDetail.props,
  id: t.number,
  d2d_no: t.string,
  user: adminUser,
  salesperson_japan: h.omittable(adminUser),
  salesperson_china: h.omittable(adminUser),
  salesperson_customs: h.omittable(adminUser),
  company: h.omittable(companyItem),
  file: h.omittable(t.string),
  memo: h.omittable(t.string),
  status: inquiryStatus,
  cancel_detail: h.omittable(t.string),
  updated_at: t.string,
  created_at: t.string,
  can_views: t.array(inquiryViewer),
  owner: h.omittable(adminUser),
  owner_company: h.omittable(companyItem),
  source: bookingInquirySource,
});

export type AdminInquiryDetail = t.TypeOf<typeof adminInquiryDetail>;

export const bookingInquiryPostResponse = t.type({
  id: t.number,
  file: h.omittable(t.string),
  d2d_no: t.string,
});

export const inquiryViewerAddForm = t.type({
  email: newEmailField(bookingInquiryStrings.followerEmail),
});

export type InquiryViewerAddForm = t.TypeOf<typeof inquiryViewerAddForm>;

export const postBookingInquiry = t.type({
  ...baseInquiryDetail.props,
  has_special_note: h.boolean(),
  owner: t.number,
  source: bookingInquirySource,
});

export type PostBookingInquiry = t.TypeOf<typeof postBookingInquiry>;

export const patchBookingInquiry = t.partial({
  ...baseInquiryDetail.props,
});

export type PatchBookingInquiry = t.TypeOf<typeof patchBookingInquiry>;

export const bookingInquiryLimits = t.type({
  max_etd: t.string,
});

export type BookingInquiryLimits = t.TypeOf<typeof bookingInquiryLimits>;

export class AdminInquiryDetailHelper {
  constructor(public data: AdminInquiryDetail) {}

  isSalesperson(user: BaseUser) {
    return [this.data.salesperson_china, this.data.salesperson_japan, this.data.salesperson_customs]
      .map((d) => d?.email)
      .includes(user.email);
  }

  getFirstFoodApplicationStatus(preAlert: PreAlertDetails): string {
    if (!this.data.owner_company?.first_food_application_d2d_no) {
      return this.data.owner_company?.first_food_application_status || '-';
    }
    return this.data.owner_company?.first_food_application_d2d_no === this.data.d2d_no ||
      dayjs(this.data.owner_company?.first_food_application_etd).isAfter(preAlert.etd)
      ? '未'
      : '済';
  }

  static isBeforeDeparture(preAlert: PreAlertDetails): boolean {
    return !preAlert.is_manually_locked;
  }

  static canMutateForLedgerLock(preAlert: PreAlertDetails): MessagesEither {
    return makeMessagesEitherLeftIf(preAlert.is_ledger_locked, {
      messages: [preAlertStrings.errorLedgerLocked],
    });
  }

  canEdit(user: BaseUser, preAlert: PreAlertDetails): MessagesEither {
    return mergeMessagesEither(
      () =>
        makeMessagesEitherRightIf(this.isSalesperson(user), {
          messages: [bookingInquiryStrings.errorSalespersonOnlyEdit],
        }),
      () => AdminInquiryDetailHelper.canMutateForLedgerLock(preAlert),
    );
  }

  canEditAsSalespersonJapan(user: BaseUser): MessagesEither {
    return makeMessagesEitherRightIf(user.email === this.data.salesperson_japan?.email, {
      messages: [bookingInquiryStrings.errorSalespersonJapanOnlyEdit],
    });
  }

  canEditAsSalespersonCustoms(user: BaseUser): MessagesEither {
    return makeMessagesEitherRightIf(user.email === this.data.salesperson_customs?.email, {
      messages: [bookingInquiryStrings.errorSalespersonCustomsOnlyEdit],
    });
  }

  static canUnlock(user: BaseUser, preAlert: PreAlertDetails): MessagesEither {
    return mergeMessagesEither(
      () => AdminInquiryDetailHelper.canMutateForLedgerLock(preAlert),
      () =>
        makeMessagesEitherRightIf(new UserHelper(user).isAdmin, {
          messages: [bookingInquiryStrings.errorUnlockAdminOnly],
        }),
    );
  }

  static canUserBecomeSalespersonJapan(user: BaseUser, preAlert: PreAlertDetails): MessagesEither {
    return mergeMessagesEither(
      () => AdminInquiryDetailHelper.canMutateForLedgerLock(preAlert),
      () =>
        makeMessagesEitherRightIf(rolesByCountry['jp'].includes(user.role as any), {
          messages: [bookingInquiryStrings.errorBecomeSalespersonJapan],
        }),
    );
  }

  static canUserBecomeSalespersonChina(user: BaseUser, preAlert: PreAlertDetails): MessagesEither {
    return mergeMessagesEither(
      () => AdminInquiryDetailHelper.canMutateForLedgerLock(preAlert),
      () =>
        makeMessagesEitherRightIf(rolesByCountry['cn'].includes(user.role as any), {
          messages: [bookingInquiryStrings.errorBecomeSalespersonChina],
        }),
    );
  }

  static canUserBecameSalespersonCustoms(
    user: BaseUser,
    preAlert: PreAlertDetails,
  ): MessagesEither {
    return mapMessagesEitherLeftOr(
      AdminInquiryDetailHelper.canUserBecomeSalespersonJapan(user, preAlert),
      () =>
        makeMessagesEitherLeft({ messages: [bookingInquiryStrings.errorBecomeSalespersonCustoms] }),
    );
  }

  static canCreateArrivalNotice(preAlert: PreAlertDetails): MessagesEither {
    return mergeMessagesEither(
      () =>
        makeMessagesEitherLeftIf(preAlert.is_empty, {
          messages: [bookingInquiryStrings.errorPreAlertIsEmpty],
        }),
      () =>
        makeMessagesEitherLeftIf(!preAlert.vessel_schedule, {
          messages: [bookingInquiryStrings.errorMissingVesselSchedule],
        }),
      () =>
        makeMessagesEitherLeftIf(!!preAlert.vessel_schedule && !preAlert.eta, {
          messages: [bookingInquiryStrings.errorMissingEta],
        }),
    );
  }

  static canExportArrivalNotice(preAlert: PreAlertDetails): MessagesEither {
    return mergeMessagesEither(
      () => this.canCreateArrivalNotice(preAlert),
      () =>
        makeMessagesEitherLeftIf(!!preAlert.eta && !Number(preAlert.exchange_rate), {
          messages: [bookingInquiryStrings.errorMissingExchangeRate],
        }),
    );
  }

  static canCreateSeaWaybill(preAlert: PreAlertDetails): MessagesEither {
    return firstLeftEither(() =>
      makeMessagesEitherLeftIf(preAlert.is_empty, {
        messages: [...bookingInquiryStrings.errorSeaWaybillPreAlertIsEmpty],
      }),
    );
  }
}
